import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard, EmailGuard, StandardURL } from "../App";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash, faPlus, faToggleOn, faToggleOff, faMoneyBillWave, faHandPointUp, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { Oval } from "react-loader-spinner";

// --- START: Reusable STK Push Modal Component (Defined outside the main component) ---
const StkPushModal = ({ isOpen, onClose, onSubmit, propertyId, phoneNumber, setPhoneNumber, loading }) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
            <div className="w-full max-w-sm transform rounded-2xl bg-white p-8 shadow-2xl">
                <h2 className="mb-6 text-xl font-bold text-gray-800">
                    Initiate STK Push (Service Charge)
                </h2>
                <p className="mb-4 text-sm text-gray-600">
                    Property ID: <span className="font-semibold text-blue-600">{propertyId}</span>
                </p>
                <form onSubmit={onSubmit}>
                    <input
                        type="tel"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        placeholder="M-Pesa Phone (e.g., 2547XXXXXXXX)"
                        className="mb-6 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:border-green-500 focus:outline-none focus:ring-1 focus:ring-green-500"
                        required
                    />
                    <div className="flex justify-end space-x-3">
                        <button
                            type="button"
                            onClick={onClose}
                            className="transform rounded-full bg-gray-400 px-6 py-2 font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-gray-500"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading}
                            className={`transform rounded-full px-6 py-2 font-semibold text-white transition-all duration-200 flex items-center justify-center ${
                                loading
                                    ? "cursor-not-allowed bg-gray-400"
                                    : "bg-green-600 hover:scale-105 hover:bg-green-700"
                            }`}
                        >
                            {loading ? (
                                <FontAwesomeIcon icon={faSpinner} spin className="h-5 w-5 mr-1" />
                            ) : (
                                <FontAwesomeIcon icon={faMoneyBillWave} className="mr-2" />
                            )}
                            Send STK Push
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};
// --- END: Reusable STK Push Modal Component ---


const ServiceChargePage = () => {
    const [serviceCharges, setServiceCharges] = useState([]);
    const [selectedPropertyId, setSelectedPropertyId] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isStkModalOpen, setIsStkModalOpen] = useState(false); // New STK modal state
    const [stkPhoneNumber, setStkPhoneNumber] = useState(""); // Phone number for STK push
    const [modalAction, setModalAction] = useState(""); // "Add" or "Update"
    const [modalData, setModalData] = useState({
        property_id: "",
        servicefee: "",
    });

    const [isLoading, setIsLoading] = useState(false); // General fetch/modal loading
    const [loadingActionId, setLoadingActionId] = useState(null); // Per-row action loading

    const token = localStorage.getItem("token");
    const config = {
        headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
        },
    };

    useEffect(() => {
        fetchServiceCharges();
    }, []);

    // 1. Fetch All (GET)
    const fetchServiceCharges = () => {
        setIsLoading(true);
        axios
            .get(`${StandardURL}/iNyumba/serviceratecharge/fetchall`, config)
            .then((response) => {
                setServiceCharges(response.data.response || []);
                toast.success("Service charges fetched!");
            })
            .catch((error) => {
                console.error("Error fetching service charges:", error);
                toast.error(error.response?.data?.response || "Failed to fetch service charges.");
                setServiceCharges([]);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // 2. Update Fee (PATCH)
    const handleUpdateServiceCharge = () => {
        setIsLoading(true);
        const { servicefee } = modalData;

        axios
            .patch(
                `${StandardURL}/iNyumba/serviceratecharge/update/${selectedPropertyId}`,
                { servicefee: Number(servicefee) },
                config
            )
            .then((response) => {
                setIsModalOpen(false);
                fetchServiceCharges();
                toast.success(response.data.response || "Service charge updated successfully!");
            })
            .catch((error) => {
                console.error("Error updating service charge:", error);
                toast.error(error.response?.data?.response || "Failed to update service charge.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // 3. Add Fee (POST)
    const handleAddServiceCharge = () => {
        setIsLoading(true);
        const { property_id, servicefee } = modalData;

        axios
            .post(
                `${StandardURL}/iNyumba/serviceratecharge/add`,
                { property_id, servicefee: Number(servicefee) },
                config
            )
            .then((response) => {
                setIsModalOpen(false);
                fetchServiceCharges();
                toast.success(response.data.response || "Service charge added successfully!");
            })
            .catch((error) => {
                console.error("Error adding service charge:", error);
                toast.error(error.response?.data?.response || "Failed to add service charge.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };
    
    // Combined handler for modal submission
    const handleModalSubmit = (e) => {
        e.preventDefault();
        if (modalAction === "Update") {
            handleUpdateServiceCharge();
        } else if (modalAction === "Add") {
            handleAddServiceCharge();
        }
    }


    // 4. Delete Fee (DELETE)
    const handleDeleteServiceCharge = (propertyId) => {
        if (!window.confirm(`Are you sure you want to delete service charge for ${propertyId}?`)) {
            return;
        }

        setLoadingActionId(propertyId);

        axios
            .delete(
                `${StandardURL}/iNyumba/serviceratecharge/delete/${propertyId}`,
                config
            )
            .then((response) => {
                fetchServiceCharges();
                toast.success(response.data.response || `Service charge for ${propertyId} deleted!`);
            })
            .catch((error) => {
                console.error("Error deleting service charge:", error);
                toast.error(error.response?.data?.response || "Failed to delete service charge.");
            })
            .finally(() => {
                setLoadingActionId(null);
            });
    };

    // 5. Toggle Status (PATCH: /updatestatus/{propertyId}) - NEW
    const handleToggleStatus = (serviceCharge) => {
        const propertyId = serviceCharge.property_id;
        const newStatusText = serviceCharge.status === "ACTIVE" ? "DEACTIVATE" : "ACTIVATE";

        if (!window.confirm(`Are you sure you want to ${newStatusText} service charge for ${propertyId}?`)) {
            return;
        }

        setLoadingActionId(propertyId);

        axios
            .patch(
                `${StandardURL}/iNyumba/serviceratecharge/updatestatus/${propertyId}`,
                {},
                config
            )
            .then((response) => {
                fetchServiceCharges();
                toast.success(response.data.response || `Status for ${propertyId} updated to ${newStatusText}!`);
            })
            .catch((error) => {
                console.error("Error updating status:", error);
                toast.error(error.response?.data?.response || "Failed to update status.");
            })
            .finally(() => {
                setLoadingActionId(null);
            });
    };

    // 6. Post Manual (POST: /postmanual/{propertyId}) - NEW
    const handlePostManual = (propertyId) => {
        if (!window.confirm(`Confirm manual service charge post for ${propertyId}?`)) {
            return;
        }

        setLoadingActionId(propertyId);

        axios
            .post(
                `${StandardURL}/iNyumba/serviceratecharge/postmanual/${propertyId}`,
                {}, // Empty JSON body
                config
            )
            .then((response) => {
                fetchServiceCharges();
                toast.success(response.data.response || `Manual post successful for ${propertyId}!`);
            })
            .catch((error) => {
                console.error("Error manual post:", error);
                toast.error(error.response?.data?.response || "Failed to perform manual post.");
            })
            .finally(() => {
                setLoadingActionId(null);
            });
    };

    // 7. STK Push (POST: /stkpushservicecharge/{propertyId}) - NEW
    const handleStkPush = (e) => {
        e.preventDefault();
        const propertyId = selectedPropertyId;
        if (!propertyId || !stkPhoneNumber) {
            toast.error("Missing property ID or phone number.");
            return;
        }

        setLoadingActionId(propertyId);

        axios
            .post(
                `${StandardURL}/iNyumba/stkpushservicecharge/${propertyId}`,
                { phone_number: stkPhoneNumber },
                config
            )
            .then((response) => {
                toast.success(response.data.response || "STK Push initiated! Check your M-Pesa phone.");
                setIsStkModalOpen(false);
                setStkPhoneNumber("");
            })
            .catch((error) => {
                console.error("Error initiating STK Push:", error);
                toast.error(error.response?.data?.response || "Failed to initiate STK Push.");
            })
            .finally(() => {
                setLoadingActionId(null);
            });
    };


    // UI Handlers
    const handleOpenUpdateModal = (serviceCharge) => {
        setSelectedPropertyId(serviceCharge.property_id);
        setModalData({
            property_id: serviceCharge.property_id,
            servicefee: serviceCharge.servicefee,
        });
        setModalAction("Update");
        setIsModalOpen(true);
    };

    const handleOpenStkModal = (serviceCharge) => {
        setSelectedPropertyId(serviceCharge.property_id);
        setStkPhoneNumber("");
        setIsStkModalOpen(true);
    };

    const handleModalInputChange = (e) => {
        const { name, value } = e.target;
        setModalData({ ...modalData, [name]: value });
    };

    // Status Badge Component
    const StatusBadge = ({ status }) => {
        const color = status === "ACTIVE" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
        return (
            <span className={`px-3 py-1 text-xs font-semibold rounded-full ${color} capitalize`}>
                {status}
            </span>
        );
    };


    return (
        <EmailGuard>
            <AuthGuard>
                <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
                    <div className="container mx-auto max-w-6xl rounded-2xl bg-white p-8 shadow-2xl">
                        <h1 className="mb-8 text-4xl font-extrabold tracking-tight text-gray-900 border-b-4 border-blue-600 pb-2">
                            Service Rate Charge Management
                        </h1>
                        <button
                            onClick={() => {
                                setModalAction("Add");
                                setModalData({ property_id: "", servicefee: "" });
                                setIsModalOpen(true);
                            }}
                            className="flex items-center rounded-xl bg-blue-600 px-6 py-3 font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-blue-700 focus:outline-none focus:ring-4 focus:ring-blue-500/50 mb-6"
                        >
                            <FontAwesomeIcon icon={faPlus} className="mr-2" />
                            Add New Rate
                        </button>
                        
                        {/* Table Section */}
                        <div className="overflow-x-auto rounded-xl shadow-xl">
                            {isLoading ? (
                                <div className="flex justify-center items-center py-10">
                                    <Oval height={30} width={30} color="#3B82F6" visible={true} ariaLabel="loading-indicator" secondaryColor="#BFDBFE" />
                                    <span className="ml-3 text-lg text-gray-600">Loading service charges...</span>
                                </div>
                            ) : (
                                <table className="min-w-full table-auto text-sm divide-y divide-gray-200">
                                    <thead className="bg-gray-100">
                                        <tr className="text-left text-xs font-semibold uppercase tracking-wider text-gray-600">
                                            <th className="px-6 py-4">Property ID</th>
                                            <th className="px-6 py-4">Service Fee (KES)</th>
                                            <th className="px-6 py-4">Status</th>
                                            <th className="px-6 py-4 text-center">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200 bg-white">
                                        {serviceCharges.map((serviceCharge) => (
                                            <tr key={serviceCharge.property_id} className="transition-colors duration-200 hover:bg-sky-50">
                                                <td className="whitespace-nowrap px-6 py-4 font-medium text-gray-900">
                                                    {serviceCharge.property_id}
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-gray-700 font-bold text-lg">
                                                    {Number(serviceCharge.servicefee).toFixed(2)} KES
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4">
                                                    <StatusBadge status={serviceCharge.status} />
                                                </td>
                                                <td className="whitespace-nowrap px-6 py-4 text-center">
                                                    <div className="flex justify-center space-x-2">
                                                        {/* 1. Update Fee */}
                                                        <button
                                                            onClick={() => handleOpenUpdateModal(serviceCharge)}
                                                            className="p-2 rounded-full text-indigo-600 hover:bg-indigo-50 transition duration-150"
                                                            title="Update Service Fee"
                                                        >
                                                            <FontAwesomeIcon icon={faEdit} />
                                                        </button>
                                                        
                                                        {/* 2. Toggle Status (NEW) */}
                                                        <button
                                                            onClick={() => handleToggleStatus(serviceCharge)}
                                                            disabled={loadingActionId === serviceCharge.property_id}
                                                            className={`p-2 rounded-full transition duration-150 disabled:opacity-50 ${
                                                                serviceCharge.status === 'ACTIVE'
                                                                    ? 'text-red-600 hover:bg-red-50'
                                                                    : 'text-green-600 hover:bg-green-50'
                                                                }`}
                                                            title={serviceCharge.status === 'ACTIVE' ? 'Deactivate' : 'Activate'}
                                                        >
                                                            {loadingActionId === serviceCharge.property_id ? (
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                            ) : (
                                                                <FontAwesomeIcon icon={serviceCharge.status === 'ACTIVE' ? faToggleOn : faToggleOff} />
                                                            )}
                                                        </button>

                                                        {/* 3. Post Manual (NEW) */}
                                                        <button
                                                            onClick={() => handlePostManual(serviceCharge.property_id)}
                                                            disabled={loadingActionId === serviceCharge.property_id}
                                                            className="p-2 rounded-full text-blue-600 hover:bg-blue-50 transition duration-150 disabled:opacity-50"
                                                            title="Manual Post"
                                                        >
                                                            {loadingActionId === serviceCharge.property_id ? (
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faHandPointUp} />
                                                            )}
                                                        </button>
                                                        
                                                        {/* 4. STK Push (NEW) */}
                                                        <button
                                                            onClick={() => handleOpenStkModal(serviceCharge)}
                                                            className="p-2 rounded-full text-green-600 hover:bg-green-50 transition duration-150"
                                                            title="STK Push Top Up"
                                                        >
                                                            <FontAwesomeIcon icon={faMoneyBillWave} />
                                                        </button>
                                                        
                                                        {/* 5. Delete */}
                                                        <button
                                                            onClick={() => handleDeleteServiceCharge(serviceCharge.property_id)}
                                                            disabled={loadingActionId === serviceCharge.property_id}
                                                            className="p-2 rounded-full text-red-600 hover:bg-red-50 transition duration-150 disabled:opacity-50"
                                                            title="Delete"
                                                        >
                                                            {loadingActionId === serviceCharge.property_id ? (
                                                                <FontAwesomeIcon icon={faSpinner} spin />
                                                            ) : (
                                                                <FontAwesomeIcon icon={faTrash} />
                                                            )}
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>

                {/* --- Add/Update Modal --- */}
                {isModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
                        <div className="w-full max-w-md transform rounded-2xl bg-white p-8 shadow-2xl">
                            <h2 className="mb-6 text-2xl font-bold text-gray-800">
                                {modalAction} Service Charge
                            </h2>
                            <form onSubmit={handleModalSubmit}>
                                {modalAction === "Add" && (
                                    <input
                                        type="text"
                                        name="property_id"
                                        value={modalData.property_id}
                                        onChange={handleModalInputChange}
                                        placeholder="Property ID"
                                        className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                        required
                                    />
                                )}
                                <input
                                    type="number"
                                    step="0.01"
                                    name="servicefee"
                                    value={modalData.servicefee}
                                    onChange={handleModalInputChange}
                                    placeholder="Service Fee (e.g., 5.0 KES)"
                                    className="mb-6 w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                                    required
                                />
                                <div className="flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="transform rounded-full bg-gray-400 px-6 py-2 font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-gray-500"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={isLoading}
                                        className={`transform rounded-full px-6 py-2 font-semibold text-white transition-all duration-200 flex items-center justify-center ${
                                            isLoading
                                                ? "cursor-not-allowed bg-gray-400"
                                                : "bg-blue-600 hover:scale-105 hover:bg-blue-700"
                                        }`}
                                    >
                                        {isLoading ? (
                                            <Oval height={20} width={20} color="white" visible={true} ariaLabel="loading-indicator" secondaryColor="white" />
                                        ) : (
                                            modalAction
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}

                {/* --- STK Push Modal --- */}
                <StkPushModal
                    isOpen={isStkModalOpen}
                    onClose={() => {
                        setIsStkModalOpen(false);
                        setStkPhoneNumber("");
                    }}
                    onSubmit={handleStkPush}
                    propertyId={selectedPropertyId}
                    phoneNumber={stkPhoneNumber}
                    setPhoneNumber={setStkPhoneNumber}
                    loading={loadingActionId} // Use loadingActionId for STK push since it's a row-specific action
                />
            </AuthGuard>
        </EmailGuard>
    );
};

export default ServiceChargePage;